import { useEffect, useMemo, useState } from "react";
import CommonModal from "@/common/components/common/CommonModal";
import { Button } from "@/common/components/ui/button";
import { Users, ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox, Switch } from "antd";
import { cn } from "@/lib/utils";
import { RM } from "./theme";
import { FinEaseAntdScope } from "./FinEaseAntdScope";
import {
  DEFAULT_ACCESS_TREE,
  buildPermissionState,
  flattenAccessIds,
  type AccessNode,
  type PermissionRowState,
} from "./manageAccessUtils";

export interface ManageAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Role or user context title */
  title: string;
  subtitle?: string;
  tree?: AccessNode[];
  /** Optional preset when editing existing role */
  initialAccess?: Record<string, Partial<PermissionRowState>>;
  onSave?: (state: Record<string, PermissionRowState>) => void;
}

export function ManageAccessModal({
  open,
  onOpenChange,
  title,
  subtitle = "Edit Individual Permissions",
  tree = DEFAULT_ACCESS_TREE,
  initialAccess,
  onSave,
}: ManageAccessModalProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [rowState, setRowState] = useState<Record<string, PermissionRowState>>(
    () => buildPermissionState(tree)
  );

  const idsKey = useMemo(() => flattenAccessIds(tree).join(","), [tree]);

  useEffect(() => {
    if (open) {
      setRowState(buildPermissionState(tree, initialAccess));
      const nextExp: Record<string, boolean> = {};
      for (const n of tree) {
        if (n.children?.length) {
          nextExp[n.id] = n.id === "ipo";
        }
      }
      setExpanded(nextExp);
    }
  }, [open, idsKey, tree, initialAccess]);

  const toggleExpanded = (id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const patchRow = (id: string, patch: Partial<PermissionRowState>) => {
    setRowState(prev => {
      const cur = prev[id] ?? {
        on: false,
        view: false,
        edit: false,
        delete: false,
      };
      return { ...prev, [id]: { ...cur, ...patch } };
    });
  };

  const renderRows = (nodes: AccessNode[], depth = 0) => {
    return nodes.map(node => {
      const hasChildren = !!node.children?.length;
      const isOpen = expanded[node.id];
      const st = rowState[node.id] ?? {
        on: false,
        view: false,
        edit: false,
        delete: false,
      };

      return (
        <div key={node.id}>
          <div className={cn(RM.permRow, depth > 0 && RM.permRowNested)}>
            {hasChildren ? (
              <button
                type="button"
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center",
                  RM.permChevron
                )}
                onClick={() => toggleExpanded(node.id)}
                aria-label={isOpen ? "Collapse" : "Expand"}
              >
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="w-8 shrink-0" />
            )}
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <Switch
                checked={st.on}
                onChange={on => {
                  patchRow(node.id, {
                    on,
                    view: on ? true : false,
                    edit: on ? true : false,
                    delete: on ? true : false,
                  });
                }}
              />
              <span className={RM.permLabel}>{node.label}</span>
            </div>
            <div className={RM.permissionColCheck}>
              <Checkbox
                checked={st.view}
                disabled={!st.on}
                onChange={e => patchRow(node.id, { view: e.target.checked })}
              />
            </div>
            <div className={RM.permissionColCheck}>
              <Checkbox
                checked={st.edit}
                disabled={!st.on}
                onChange={e => patchRow(node.id, { edit: e.target.checked })}
              />
            </div>
            <div className={RM.permissionColCheck}>
              <Checkbox
                checked={st.delete}
                disabled={!st.on}
                onChange={e => patchRow(node.id, { delete: e.target.checked })}
              />
            </div>
          </div>
          {hasChildren && isOpen && (
            <div className={RM.permNestedBlock}>
              {renderRows(node.children!, depth + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <CommonModal
      open={open}
      onCancel={() => onOpenChange(false)}
      modalRender={node => <FinEaseAntdScope>{node}</FinEaseAntdScope>}
      title={
        <div className="flex items-start gap-3 text-left">
          <div className={RM.manageAccessIconWrap}>
            <Users className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className={RM.modalTitle}>{title}</div>
            <p className="mt-1 text-sm font-normal text-muted-foreground">
              {subtitle}
            </p>
          </div>
        </div>
      }
      width={672}
      destroyOnClose
      maskClosable={false}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className={RM.btnPrimary}
            onClick={() => {
              onSave?.(rowState);
              onOpenChange(false);
            }}
          >
            Save
          </Button>
        </div>
      }
      bodyStyle={{
        maxHeight: "min(70vh, 520px)",
        overflowY: "auto",
        padding: "12px 16px",
      }}
    >
      <div className={RM.permissionTableShell}>
        <div className={RM.permissionTableHeader}>
          <div className="w-8 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1 text-left">Module</div>
          <div className={RM.permissionColCheck}>View</div>
          <div className={RM.permissionColCheck}>Edit</div>
          <div className={RM.permissionColCheck}>Delete</div>
        </div>
        {renderRows(tree)}
      </div>
    </CommonModal>
  );
}
